/**
 * UT Matrix 전기사양 비교 분석 — 데이터 조회/저장
 * REST 엔드포인트(apis/ut/UtComparisonElec.ts → /v1/ut/ut-comparison-elec/*)를 호출한다.
 * compareElec.ts 등 나머지 화면 로직은 이 파일이 반환하는 형태(ElecSourceRow/
 * ElecDiffRow)에만 의존한다.
 *
 * 대상 UT Matrix 선택 목록(fetchUtMatrixList)은 "UT 사용량 비교(기계)" 화면과 공유한다.
 */
import { fetchUtMatrixList } from '../api';
import {
  findElecDetail,
  findReportDetail,
  findRmk,
  saveReport as saveReportApi,
  updateRmk as updateRmkApi,
} from 'apis/ut/UtComparisonElec';
import {
  ElecRptDetail as ElecRptDetailModel,
  ElecSourceRow as ElecSourceRowModel,
} from 'models/ut/UtComparisonElec';
import { SaveReportResult } from 'models/ut/UtMatrixComparisonReportList';
import { ElecDiffRow, ElecRptDetail, ElecSourceRow, UtMatrixOption } from './types';
import { withBreakerAnalysis } from './utils/compareElec';

export { fetchUtMatrixList };

export const errorMessage = (err: unknown): string => {
  if (err instanceof Error) return err.message;
  if (err && typeof err === 'object' && 'message' in err) return String((err as any).message);
  if (err && typeof err === 'object' && 'data' in err) {
    const d = (err as any).data;
    if (typeof d === 'string') return d;
    if (d && typeof d === 'object' && 'message' in d) return String(d.message);
  }
  return String(err);
};

/**
 * 저장 권한 여부 — 실질적인 저장 권한 검증은 백엔드가 최종적으로 담당한다.
 * (프런트의 이 값은 버튼 비활성화 등 UX 용도일 뿐 신뢰 경계가 아니다)
 */
export const canExecuteDml = (): boolean => true;

/** 전압유형 표시문자열 — voltage/phase/wires 중 하나라도 없으면 'UNKNOWN' */
const buildVt = (vltgNvl: number | null, elpwPhasCd: string, ctwTpCd: string): string => {
  if (vltgNvl == null || !elpwPhasCd || !ctwTpCd) return 'UNKNOWN';
  return `${vltgNvl}V ${elpwPhasCd}${ctwTpCd}`;
};

const toElecSourceRow = (r: ElecSourceRowModel): ElecSourceRow => ({
  ...r,
  vltgNvl: r.vltgNvl ?? null,
  elpwPhasCd: r.elpwPhasCd ?? '',
  ctwTpCd: r.ctwTpCd ?? '',
  // 역율 — 차단기 여유율 판정(classifyBreaker)의 K값 계산에 쓴다
  pwftNvl: r.pwftNvl ?? null,
  vt: buildVt(r.vltgNvl ?? null, r.elpwPhasCd ?? '', r.ctwTpCd ?? ''),
});

/** 전기 원천 데이터 — 정확히 2개 UT Matrix */
export const fetchElecDetail = async (utmIds: string[]): Promise<ElecSourceRow[]> => {
  const rows = await findElecDetail(utmIds);
  return rows.map(toElecSourceRow);
};

export interface SaveElecRptInput {
  rptNm: string;
  selectedMatrices: UtMatrixOption[]; // 정확히 2개 (전/후 순서)
  diffRows: ElecDiffRow[];
  userId: string;
}

/** 이력 저장 — 성공 시 RPT_ID + 중복 여부 반환 */
export const saveElecRpt = async (input: SaveElecRptInput): Promise<SaveReportResult> => {
  if (input.selectedMatrices.length !== 2) {
    throw new Error('비교 대상 UT Matrix는 정확히 2개여야 저장할 수 있습니다.');
  }
  return saveReportApi({
    rptNm: input.rptNm,
    selectedMatrices: input.selectedMatrices,
    // 차단기 판정(부족/적정/과다)은 원천의 역률·전압이 있어야 나오는데 RPT_ELPW_D에는
    // 그 값이 없다. 저장 시점에 문구를 붙여야 나중에 여유율 집계를 할 수 있다.
    diffRows: withBreakerAnalysis(input.diffRows),
    userId: input.userId,
  });
};

/** 전체 종합의견(RMK)만 경량 조회 — ElecOpinionPanel이 historyRptId 로드 시 사용 */
export const fetchElecRmk = async (rptId: string): Promise<string> => findRmk(rptId);

/** 저장된 이력 상세 조회 — UtMatrixComparisonReportListPage에서도 사용 (구분=전기 행 클릭 시) */
export const fetchRptElecDetail = async (rptId: string): Promise<ElecRptDetail> => {
  const detail: ElecRptDetailModel = await findReportDetail(rptId);
  return detail as ElecRptDetail; // 필드 구성이 1:1 동일 (types.ts 주석 참고)
};

/**
 * 저장된 이력(ElecRptDetail)을 화면이 그대로 그릴 수 있는 ElecDiffRow[]로 되돌린다.
 * RPT_ELPW_D는 1행 = 비교결과 1행이므로 그대로 매핑하면 되고, 원본 부속필드
 * (vltgNvl/elpwPhasCd/ctwTpCd)는 표시용 VLTG_TP_NM 하나로만 저장했으므로 재현하지 않는다
 * (재계산에 쓰이지 않고 화면 표시(vt)에만 쓰이기 때문). 역률(pwftNvl)도 마찬가지로 없어서
 * 차단기 판정은 재계산하지 않고 저장해둔 brkAnlsCtn을 그대로 쓴다.
 */
export const elecRptDetailToDiffRows = (detail: ElecRptDetail): ElecDiffRow[] => {
  const bfUtmId = detail.targets[0]?.utmId ?? '';
  const afUtmId = detail.targets[1]?.utmId ?? '';
  return detail.details.map((d, i) => {
    const hasBefore = d.difTpCd !== 'ADD';
    const hasAfter = d.difTpCd !== 'DEL';
    const base = {
      stndEqpId: d.stndEqpId,
      prdnProcNm: d.prdnProcNm,
      eqclNm: d.eqclNm,
      dtalProcNm: d.dtalProcNm,
      bizAreaNm: d.bizAreaNm,
      bldgFloCd: d.bldgFloCd,
      istlLocNm: d.istlLocNm,
      eqpMainNm: d.eqpMainNm,
      eqpMchNm: d.eqpMchNm,
      vltgNvl: null,
      elpwPhasCd: '',
      ctwTpCd: '',
      // 역률도 저장돼 있지 않다 — 차단기 판정은 재계산 대신 저장된 brkAnlsCtn을 쓴다
      pwftNvl: null,
      vt: d.vltgTpNm && d.vltgTpNm !== '-' ? d.vltgTpNm : 'UNKNOWN',
      elpwEqpCnctTpCd: d.elpwEqpCnctTpCd,
    };
    const before: ElecSourceRow | null = hasBefore
      ? {
          ...base,
          utmId: bfUtmId,
          prdnLnNm: d.prdnLnNmBf,
          elpwEqpQty: d.elpwEqpQtyBf ?? 0,
          brkeCapa: d.brkeCapaBf,
          elpwCapa: d.elpwCapaBf,
          elpwSumCapa: d.elpwSumCapaBf,
          dmdElpwTarfNvl: d.dmdElpwTarfNvlBf,
          dmdElpwCapa: d.dmdElpwCapaBf,
          // 역률/전압이 저장돼 있지 않아 재판정이 불가능하다 — 저장 당시 문구를 그대로 쓴다
          brkAnlsCtn: d.brkeAnlsCtnBf || undefined,
        }
      : null;
    const after: ElecSourceRow | null = hasAfter
      ? {
          ...base,
          utmId: afUtmId,
          prdnLnNm: d.prdnLnNmAf,
          elpwEqpQty: d.elpwEqpQtyAf ?? 0,
          brkeCapa: d.brkeCapaAf,
          elpwCapa: d.elpwCapaAf,
          elpwSumCapa: d.elpwSumCapaAf,
          dmdElpwTarfNvl: d.dmdElpwTarfNvlAf,
          dmdElpwCapa: d.dmdElpwCapaAf,
          brkAnlsCtn: d.brkeAnlsCtnAf || undefined,
        }
      : null;
    return { key: `hist-${i}`, difTpCd: d.difTpCd, difTpCtn: d.difTpCtn, before, after };
  });
};

/** 전체 종합의견(RMK) 저장 */
export const updateElecOverallRmk = async (
  rptId: string,
  rmk: string,
  userId: string
): Promise<void> => updateRmkApi(rptId, rmk, userId);
